// Investor profit-share — settled weekly. Each investor takes `sharePct` of the
// business GROSS revenue earned this week. Recorded as an expense txn so it shows
// up honestly on the cashflow meter (category 'expense' → reduces cashflow).
//
// This is the cost-of-equity lesson: unlike a loan, the money is never repaid,
// but the investor keeps taking their slice for as long as they hold the stake.

import type { GameState, Txn } from '../state/types.js';
import { applyTxn } from '../ledger/append.js';

/** Gross business revenue recorded in the ledger for a given week. */
export function grossRevenueForWeek(state: GameState, week: number): number {
  return state.ledger
    .filter((t: Txn) => t.week === week && t.type === 'revenue')
    .reduce((sum, t) => sum + t.amount, 0);
}

/** Total fraction of revenue currently owed to all investors (0..1, clamped). */
export function totalInvestorShare(state: GameState): number {
  const investors = state.investors ?? [];
  const total = investors.reduce((sum, inv) => sum + inv.sharePct, 0);
  return Math.min(1, Math.max(0, total));
}

/**
 * Skim each investor's share of this week's gross business revenue.
 * Called inside SettleWeek AFTER businesses have operated (so revenue exists in
 * the ledger for `settlingWeek`). One txn per investor; zero-payout weeks are
 * skipped so the ledger stays clean.
 */
export function settleInvestorPayouts(state: GameState, settlingWeek: number): GameState {
  const investors = state.investors ?? [];
  if (investors.length === 0) return state;

  const gross = grossRevenueForWeek(state, settlingWeek);
  if (gross <= 0) return state;

  let s = state;
  for (const inv of investors) {
    const payout = gross * inv.sharePct;
    if (payout <= 0) continue;
    s = applyTxn(s, {
      type: 'investor_payout',
      amount: -payout,
      category: 'expense',
      note: `Investor share — ${(inv.sharePct * 100).toFixed(0)}% of B$${gross.toFixed(0)} revenue`,
    });
  }
  return s;
}
